import {useCallback, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {
  selectGameResults,
  selectGameType,
  selectIsGameStarted,
  selectPhase,
  selectTimerEnabled,
  selectTurnDurationMs,
  selectTurnEndTimestamp,
  selectWarPhaseStage,
} from '../../redux/reducers/sharedStateReducer/selectors';
import {selectServerTimeDelta} from '../../redux/reducers/privateStateReducer/selectors';
import {checkIfTurnStateStale} from '../../utils/network/checkIfTurnStateStale';
import {sendNetworkMessage} from '../../utils/networkSocket';
import {sendNetworkEvent} from '../../analytics/gameAnalytics';
import {GAME_TYPES} from '../../constants/game';

// Насколько ход должен просрочить свой дедлайн, чтобы считать состояние
// протухшим. Сервер завершает ход своим таймером — на отсрочку позже срока
// игрока (TURN_AUTO_END_GRACE_MS в utils/phase/turnTimeLimits, секунда) — и
// рассылает экшены сразу, так что счет идет на секунду с небольшим:
// пятнадцати секунд хватает с запасом, чтобы не дергать сервер из-за
// расхождения часов или задержки сети
const TURN_STALE_GRACE_MS = 15000;
const STALE_CHECK_INTERVAL_MS = 5000;
// Чаще этого полное состояние не просим: оно самое тяжелое в протоколе
const RESYNC_REQUEST_COOLDOWN_MS = 10000;

/**
 * Сторож протухшего состояния матча.
 *
 * Ловит случай, когда сокет жив (и потому никаких обрывов никто не показывает),
 * а экшены до клиента не доехали: игрок видит чужой ход, которого давно нет.
 * Причина бывает разной — потерянная рассылка, полуоткрытое соединение,
 * отбракованный гвардом фазы пакет смены хода, — а лечение одно: попросить у
 * сервера полное состояние (он ответит тем же LOAD_STATE, что и при
 * переподключении).
 *
 * Две зацепки:
 *  - дедлайн текущего хода просрочен, а нового экшена нет;
 *  - вкладка вернулась из фона (на телефоне таймеры в фоне душатся, и матч
 *    мог уехать далеко вперед, пока экран был погашен).
 */
export const useStaleGameStateWatchdog = () => {
  const gameType = useSelector(selectGameType);
  const isGameStarted = useSelector(selectIsGameStarted);
  const gameResults = useSelector(selectGameResults);
  const phase = useSelector(selectPhase);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const turnEndTimestamp = useSelector(selectTurnEndTimestamp);
  const timerEnabled = useSelector(selectTimerEnabled);
  // Дедлайн этапа — по самому долгому времени среди тех, кого еще ждут (без
  // зрителя, см. selectTurnDurationMs): в промо-матче против ИИ агент думает до
  // 10 минут, и стандартный дедлайн «просрочивался» бы на каждом его ходу —
  // сторож дергал бы сервер ложными запросами состояния. В обычной партии это
  // и есть стандартное время фазы
  const turnDurationMs = useSelector(selectTurnDurationMs);
  const serverTimeDelta = useSelector(selectServerTimeDelta);

  const lastRequestAtRef = useRef(0);

  const isMatchInProgress = gameType === GAME_TYPES.ONLINE
    && isGameStarted
    && !(gameResults && gameResults.length > 0);

  const requestState = useCallback((reason) => {
    const now = Date.now();
    if (now - lastRequestAtRef.current < RESYNC_REQUEST_COOLDOWN_MS) return;
    if (!sendNetworkMessage({type: 'REQUEST_STATE'})) return;

    lastRequestAtRef.current = now;
    console.warn('[Resync] requesting full state from server:', reason);
    sendNetworkEvent('state_resync_requested', reason);
  }, []);

  useEffect(() => {
    if (!isMatchInProgress || !timerEnabled) return undefined;

    const intervalId = setInterval(() => {
      const isStale = checkIfTurnStateStale({
        turnEndTimestamp,
        phaseDurationMs: turnDurationMs,
        nowMs: Date.now() + serverTimeDelta,
        graceMs: TURN_STALE_GRACE_MS,
      });
      if (isStale) {
        requestState('stale_turn');
      }
    }, STALE_CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [
    isMatchInProgress, timerEnabled, phase, warPhaseStage, turnDurationMs,
    turnEndTimestamp, serverTimeDelta, requestState,
  ]);

  useEffect(() => {
    if (!isMatchInProgress) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      requestState('foreground');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isMatchInProgress, requestState]);
};
